/**
 * TopPlayersSection — "Top 3 jugadores" per team (EPIC-107, spec D4).
 * Rendered inside MatchAnalytics on both the cockpit analytics tab and the
 * public share page (gated there by the `top_players` share key). Rows show
 * rank, jersey, name, minutes played and the player's two strongest
 * contributions as text; the composite score is never displayed.
 */
import TeamLogo, { type TeamLogoProps } from "../../../../components/TeamLogo";
import type {
  TopPlayerContribution,
  TopPlayerRow,
} from "../../utils/topPlayers";
import type { TopPlayerStatKey } from "../../utils/topPlayers";

export interface TopPlayersSectionTheme {
  label: string;
  value: string;
  valueAlt: string;
  subtle: string;
  row: string;
  border: string;
}

interface Props {
  homeName: string;
  awayName: string;
  home: TopPlayerRow[];
  away: TopPlayerRow[];
  /** Team objects for the header crests (PMA-PUB-024). Optional. */
  homeTeam?: TeamLogoProps["team"];
  awayTeam?: TeamLogoProps["team"];
  theme: TopPlayersSectionTheme;
  t: any;
}

/** i18n key suffixes (singular, plural) per contribution stat. */
const CONTRIB_KEYS: Record<TopPlayerStatKey, [string, string]> = {
  goals: ["goal", "goals"],
  assists: ["assist", "assists"],
  shotsOnTarget: ["shotOnTarget", "shotsOnTarget"],
  passesGood: ["pass", "passes"],
  duelsWon: ["duelWon", "duelsWon"],
  recoveries: ["recovery", "recoveries"],
  interceptions: ["interception", "interceptions"],
  saves: ["save", "saves"],
  // Negative-weight stats are never shown as a "contribution".
  foulsCommitted: ["goal", "goals"],
  yellowCards: ["goal", "goals"],
  redCards: ["goal", "goals"],
  ownGoals: ["goal", "goals"],
};

const FALLBACK: Record<string, string> = {
  goal: "{{count}} goal",
  goals: "{{count}} goals",
  assist: "{{count}} assist",
  assists: "{{count}} assists",
  shotOnTarget: "{{count}} shot on target",
  shotsOnTarget: "{{count}} shots on target",
  pass: "{{count}} pass",
  passes: "{{count}} passes",
  duelWon: "{{count}} duel won",
  duelsWon: "{{count}} duels won",
  recovery: "{{count}} recovery",
  recoveries: "{{count}} recoveries",
  interception: "{{count}} interception",
  interceptions: "{{count}} interceptions",
  save: "{{count}} save",
  saves: "{{count}} saves",
};

// Hand substitution: safe whether `t` returns the translation untouched or
// the bare fallback (the PublicTeamSheet `interpolateCount` pattern).
const fill = (text: string, count: number): string =>
  text.split("{{count}}").join(String(count));

const contributionText = (c: TopPlayerContribution, t: any): string => {
  const [one, many] = CONTRIB_KEYS[c.key];
  const suffix = c.count === 1 ? one : many;
  return fill(t(`analytics.top3.${suffix}`, FALLBACK[suffix]), c.count);
};

function TeamColumn({
  side,
  name,
  team,
  rows,
  theme,
  t,
}: {
  side: "home" | "away";
  name: string;
  team?: TeamLogoProps["team"];
  rows: TopPlayerRow[];
  theme: TopPlayersSectionTheme;
  t: any;
}) {
  return (
    <div
      className={`rounded-xl p-3 ${theme.row}`}
      data-testid={`top-players-${side}`}
    >
      <div className="mb-2 flex items-center gap-2">
        {team && (
          <TeamLogo
            team={team}
            bare
            size="sm"
            testId={`top-players-${side}-crest`}
          />
        )}
        <div
          className={`min-w-0 flex-1 truncate text-base font-semibold ${theme.valueAlt}`}
          data-testid={`top-players-${side}-name`}
        >
          {name}
        </div>
        {rows.length > 0 && (
          <div
            className={`shrink-0 text-right text-xs uppercase tracking-wide ${theme.label}`}
            data-testid={`top-players-${side}-minutes-header`}
          >
            {t("analytics.top3.minutesHeader", "Min. played")}
          </div>
        )}
      </div>
      {rows.length === 0 ? (
        <div
          className={`text-sm ${theme.subtle}`}
          data-testid={`top-players-${side}-empty`}
        >
          {t("analytics.top3.empty", "No players with touches yet")}
        </div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.playerId}
              className={`flex items-center gap-3 border-b pb-2 last:border-b-0 last:pb-0 ${theme.border}`}
              data-testid={`top-players-row-${side}-${r.rank}`}
            >
              <span
                className={`w-5 shrink-0 text-center text-base font-bold ${theme.label}`}
                data-testid={`top-players-row-${side}-${r.rank}-rank`}
              >
                {r.rank}
              </span>
              <span
                className={`w-8 shrink-0 text-center text-sm font-semibold tabular-nums ${theme.value}`}
                data-testid={`top-players-row-${side}-${r.rank}-jersey`}
              >
                #{r.jerseyNumber}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={`block truncate text-sm font-semibold ${theme.valueAlt}`}
                  data-testid={`top-players-row-${side}-${r.rank}-player`}
                >
                  {r.playerName}
                </span>
                <span
                  className={`block text-sm ${theme.subtle}`}
                  data-testid={`top-players-row-${side}-${r.rank}-contrib`}
                >
                  {r.contributions
                    .map((c) => contributionText(c, t))
                    .join(" · ")}
                </span>
              </span>
              <span
                className={`shrink-0 text-sm font-semibold tabular-nums ${theme.value}`}
                data-testid={`top-players-row-${side}-${r.rank}-minutes`}
              >
                {fill(t("analytics.top3.minutes", "{{count}}'"), r.minutes)}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

export default function TopPlayersSection({
  homeName,
  awayName,
  home,
  away,
  homeTeam,
  awayTeam,
  theme,
  t,
}: Props) {
  return (
    <div className="mt-6 space-y-2" data-testid="top-players-section">
      <div
        className={`text-sm uppercase tracking-wide mb-2 ${theme.label}`}
        data-testid="top-players-title"
      >
        {t("analytics.top3.title", "Top 3 players")}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TeamColumn
          side="home"
          name={homeName}
          team={homeTeam}
          rows={home}
          theme={theme}
          t={t}
        />
        <TeamColumn
          side="away"
          name={awayName}
          team={awayTeam}
          rows={away}
          theme={theme}
          t={t}
        />
      </div>
    </div>
  );
}
